import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const InputSchema = z.object({
  beforeImageDataUrl: z
    .string()
    .min(50)
    .max(20_000_000)
    .regex(/^data:image\/(jpeg|jpg|png|webp);base64,/i),
  prompt: z.string().min(10).max(2000),
});

export const regenerateAfterImage = createServerFn({ method: "POST" })
  .inputValidator((input) => InputSchema.parse(input))
  .handler(async ({ data }) => {
    const apiKey = process.env.LOVABLE_API_KEY;
    if (!apiKey) throw new Error("LOVABLE_API_KEY missing");

    const model = "google/gemini-3-flash-image-preview";
    const startedAt = Date.now();
    const res = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        model,
        modalities: ["image", "text"],
        messages: [
          {
            role: "user",
            content: [
              { type: "text", text: data.prompt },
              { type: "image_url", image_url: { url: data.beforeImageDataUrl } },
            ],
          },
        ],
      }),
    });

    if (!res.ok) {
      const txt = await res.text();
      throw new Error(`AI gateway ${res.status}: ${txt.slice(0, 200)}`);
    }

    const json: any = await res.json();
    const msg = json?.choices?.[0]?.message;
    const url: string | undefined =
      msg?.images?.[0]?.image_url?.url ??
      (Array.isArray(msg?.content)
        ? msg.content.find((c: any) => c?.type === "image_url")?.image_url?.url
        : undefined);

    if (!url || !url.startsWith("data:image/")) {
      throw new Error("No image returned by the model");
    }
    const elapsedMs = Date.now() - startedAt;
    // Rough byte estimation from base64 payload length.
    const b64 = url.split(",")[1] ?? "";
    const approxBytes = Math.floor((b64.length * 3) / 4);
    return { dataUrl: url, meta: { model, elapsedMs, approxBytes, generatedAt: Date.now() } };
  });